import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { Icon, type IconName } from '../../components/icons';
import { cx } from '../../components/ui';

/*
 * The platform console: the part of the dashboard only the platform's own admins see. It is
 * deliberately a different room from the recruiter pages: night sky, mono figures, hairline
 * tables, so that nobody mistakes what is a customer's workspace for what runs the business.
 */

const TABS: Array<{ to: string; label: string; icon: IconName; end?: boolean }> = [
  { to: '/admin', label: 'Overview', icon: 'gauge', end: true },
  { to: '/admin/companies', label: 'Companies', icon: 'building' },
  { to: '/admin/system', label: 'System', icon: 'settings' },
];

export function AdminShell({ title, eyebrow = 'Platform console', subtitle, actions, children }: { title: ReactNode; eyebrow?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="relative -mx-4 -my-8 min-h-[calc(100vh-4rem)] overflow-hidden bg-night px-4 py-8 text-white sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      <span className="drift pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-[#7e2ea6]/25 blur-3xl" aria-hidden />
      <span className="drift-slow pointer-events-none absolute -left-32 bottom-0 h-[460px] w-[460px] rounded-full bg-[#d6217f]/15 blur-3xl" aria-hidden />
      <div className="relative mx-auto max-w-[1320px]">
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                cx('inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition', isActive ? 'bg-white text-ink shadow-brand' : 'bg-white/8 text-white/70 ring-1 ring-inset ring-white/10 hover:bg-white/12 hover:text-white')
              }
            >
              <Icon name={t.icon} className="h-4 w-4" />
              {t.label}
            </NavLink>
          ))}
        </div>
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#f7a6cf]">{eyebrow}</p>
            <h1 className="display mt-2 text-[34px] font-medium leading-none sm:text-[42px]">{title}</h1>
            {subtitle && <p className="mt-3 max-w-2xl text-[15px] text-white/60">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}

/** A console tile: a mono label, a big figure, a note. */
export function Tile({ label, value, note, tone = 'default', className }: { label: ReactNode; value: ReactNode; note?: ReactNode; tone?: 'default' | 'brand' | 'good' | 'warn'; className?: string }) {
  const colour = { default: 'text-white', brand: 'text-brand-gradient', good: 'text-[#4ade80]', warn: 'text-[#fbbf24]' }[tone];
  return (
    <div className={cx('rounded-lg bg-white/[0.05] p-5 ring-1 ring-white/10 backdrop-blur', className)}>
      <p className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-white/50">{label}</p>
      <p className={cx('display mt-3 text-[32px] font-medium leading-none tabular', colour)}>{value}</p>
      {note && <p className="mt-2.5 text-xs text-white/50">{note}</p>}
    </div>
  );
}

export function Panel({ title, description, actions, children, className, padding = true }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; padding?: boolean }) {
  return (
    <section className={cx('overflow-hidden rounded-lg bg-white/[0.04] ring-1 ring-white/10 backdrop-blur', className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold">{title}</h2>
            {description && <p className="mt-0.5 text-sm text-white/55">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={padding ? 'p-5' : ''}>{children}</div>
    </section>
  );
}

/** Table cell classes for the console's hairline tables. */
export const ATH = 'px-4 py-2.5 text-left font-mono text-[10.5px] font-medium uppercase tracking-[0.12em] text-white/45';
export const ATD = 'px-4 py-3 align-top text-sm';
export const ATR = 'border-t border-white/8 transition hover:bg-white/[0.04]';

/** Form controls in the console: the same shape as the kit's, on glass. */
export const AINPUT =
  'block h-10 w-full rounded-sm border-0 bg-white/8 px-3.5 text-sm text-white ring-1 ring-inset ring-white/15 placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[#d769cf] disabled:opacity-50';

export function AField({ label, hint, children, className }: { label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={cx('block min-w-0', className)}>
      <span className="mb-1.5 block text-[13px] font-semibold text-white/85">{label}</span>
      {children}
      {hint && <span className="mt-1.5 block text-xs text-white/45">{hint}</span>}
    </label>
  );
}

export function AButton({ children, onClick, primary, danger, disabled, type = 'button', loading, className }: { children: ReactNode; onClick?: () => void; primary?: boolean; danger?: boolean; disabled?: boolean; type?: 'button' | 'submit'; loading?: boolean; className?: string }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={cx(
        'inline-flex h-9 items-center gap-2 rounded-sm px-3.5 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50',
        primary ? 'bg-brand-gradient text-white shadow-brand hover:brightness-110' : danger ? 'bg-white/8 text-[#fda4b4] ring-1 ring-inset ring-white/15 hover:bg-white/12' : 'bg-white/8 text-white ring-1 ring-inset ring-white/15 hover:bg-white/12',
        className,
      )}
    >
      {loading && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-hidden />}
      {children}
    </button>
  );
}

export function StatusDot({ on, label }: { on: boolean; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 font-mono text-xs">
      <span className={cx('h-2 w-2 rounded-full', on ? 'bg-[#4ade80] shadow-[0_0_10px_#4ade80]' : 'bg-[#fbbf24] shadow-[0_0_10px_#fbbf24]')} aria-hidden />
      <span className={on ? 'text-[#4ade80]' : 'text-[#fbbf24]'}>{label}</span>
    </span>
  );
}

/** Six months of usage as bars. */
export function UsageBars({ history, included }: { history: Array<{ year: number; month: number; usage: { interviews: number; minutes: number } }>; included: number }) {
  const max = Math.max(1, included, ...history.map((h) => h.usage.interviews));
  return (
    <div className="flex h-36 items-end gap-2">
      {history.map((h) => {
        const pct = (h.usage.interviews / max) * 100;
        const over = h.usage.interviews > included && included > 0;
        return (
          <div key={`${h.year}-${h.month}`} className="group flex min-w-0 flex-1 flex-col items-center gap-1.5">
            <span className="font-mono text-[10px] tabular text-white/60">{h.usage.interviews}</span>
            <div className="relative flex h-24 w-full items-end rounded-[6px] bg-white/5">
              {included > 0 && <span className="absolute inset-x-0 border-t border-dashed border-white/25" style={{ bottom: `${Math.min(100, (included / max) * 100)}%` }} aria-hidden />}
              <div className={cx('w-full rounded-[6px] transition-[height] duration-500', over ? 'bg-[#fbbf24]/80' : 'bg-brand-gradient')} style={{ height: `${Math.max(pct, h.usage.interviews ? 4 : 0)}%` }} />
            </div>
            <span className="font-mono text-[10px] uppercase text-white/45">{new Date(Date.UTC(h.year, h.month - 1, 1)).toLocaleDateString('en-IN', { month: 'short', timeZone: 'UTC' })}</span>
          </div>
        );
      })}
    </div>
  );
}
